import { runtimeConfig } from '@/config/runtime-config'
import { portalLoginRoutePath } from '@/lib/auth/portal-routes'
import { clearPortalSession } from '@/lib/auth/portal-session'
import { getStoredSessionSource, getStoredToken, type PortalKind } from '@/lib/auth/token-storage'

interface LogoutSuccessResponse {
  code: 'success'
  data?: {
    logout_url?: string
  }
}

interface PortalLogoutOptions {
  includeCredentials?: boolean
}

const logoutEndpointPath: Record<PortalKind, string> = {
  admin: '/admin/auth/logout',
  user: '/app/auth/logout',
}

function resolveApiUrl(path: string) {
  const baseUrl = runtimeConfig.apiBaseUrl.endsWith('/')
    ? runtimeConfig.apiBaseUrl.slice(0, -1)
    : runtimeConfig.apiBaseUrl
  const normalizedPath = path.startsWith('/') ? path : `/${path}`

  if (baseUrl.startsWith('http')) {
    return `${baseUrl}${normalizedPath}`
  }

  return new URL(`${baseUrl}${normalizedPath}`, window.location.origin).toString()
}

async function requestPortalLogout(portal: PortalKind, options: PortalLogoutOptions = {}) {
  const token = getStoredToken(portal)
  const requestInit: RequestInit = {
    headers: token
      ? {
          authorization: `Bearer ${token}`,
        }
      : undefined,
    method: 'POST',
  }
  if (options.includeCredentials) {
    requestInit.credentials = 'include'
  }

  const response = await fetch(resolveApiUrl(logoutEndpointPath[portal]), requestInit).catch(
    () => undefined,
  )

  if (!response?.ok) {
    return undefined
  }

  const body = (await response.json().catch(() => undefined)) as
    | Partial<LogoutSuccessResponse>
    | undefined

  return body?.code === 'success' ? body.data?.logout_url : undefined
}

export function logoutPortal(portal: PortalKind) {
  void (async () => {
    const sessionSource = getStoredSessionSource(portal)
    const isUserSsoSession = portal === 'user' && sessionSource === 'sso'
    const logoutUrl = await requestPortalLogout(portal, {
      // 用户 logout 需要让 API origin 接收/清除 IdP browser-session cookie。
      includeCredentials: portal === 'user',
    })
    clearPortalSession(portal)
    const shouldUseSsoLogout = isUserSsoSession && logoutUrl
    window.location.assign(shouldUseSsoLogout ? logoutUrl : portalLoginRoutePath[portal])
  })()
}
